import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Clock, CheckCircle2, AlertCircle, Calendar, FolderKanban,
  Filter, Search, ChevronRight, Timer, Target, Loader2
} from 'lucide-react';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  pending: { label: 'À faire', color: 'bg-gray-100 text-gray-700', icon: Clock },
  in_progress: { label: 'En cours', color: 'bg-blue-100 text-blue-700', icon: Timer },
  review: { label: 'En révision', color: 'bg-orange-100 text-orange-700', icon: AlertCircle },
  completed: { label: 'Terminée', color: 'bg-green-100 text-green-700', icon: CheckCircle2 }
};

const PRIORITY_CONFIG = {
  low: { label: 'Basse', color: 'bg-gray-100 text-gray-600' },
  medium: { label: 'Moyenne', color: 'bg-yellow-100 text-yellow-700' },
  high: { label: 'Haute', color: 'bg-orange-100 text-orange-700' },
  urgent: { label: 'Urgente', color: 'bg-red-100 text-red-700' }
};

const TeamTasksPage = () => {
  const { token, user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    in_progress: 0,
    completed: 0,
    overdue: 0
  });

  // Fetch tasks
  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all tasks for all projects
      const projectsRes = await api.get('/projects/');
      const projects = projectsRes.data || [];
      
      // Fetch tasks for each project
      const allTasks = [];
      for (const project of projects) {
        try {
          const tasksRes = await api.get(`/tasks/project/${project.id}`);
          const projectTasks = tasksRes.data || [];
          projectTasks.forEach(task => {
            allTasks.push({
              ...task,
              title: task.name,
              project_name: project.name,
              project_id: project.id,
              priority: task.priority || 'medium'
            });
          });
        } catch (e) {
          // Skip projects without tasks endpoint
        }
      }
      
      setTasks(allTasks);
      
      // Calculate stats
      const now = new Date();
      setStats({
        total: allTasks.length,
        pending: allTasks.filter(t => t.status === 'pending').length,
        in_progress: allTasks.filter(t => t.status === 'in_progress' || t.status === 'awaiting_client').length,
        completed: allTasks.filter(t => t.status === 'completed').length,
        overdue: allTasks.filter(t => t.deadline && new Date(t.deadline) < now && t.status !== 'completed').length
      });
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Erreur lors du chargement des tâches');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = (task.title || task.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         task.project_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || task.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || task.priority === filterPriority;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return { text: `${Math.abs(diffDays)}j en retard`, color: 'text-red-600' };
    if (diffDays === 0) return { text: "Aujourd'hui", color: 'text-orange-600' };
    if (diffDays === 1) return { text: 'Demain', color: 'text-orange-600' };
    if (diffDays <= 7) return { text: `Dans ${diffDays}j`, color: 'text-blue-600' };
    return { text: date.toLocaleDateString('fr-FR'), color: 'text-gray-600' };
  };

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={cn("p-2 rounded-lg", color)}>
            <Icon className="h-4 w-4" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">{title}</p>
            <p className="text-lg font-bold">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      
      <div className={cn(
        "flex-1 flex flex-col transition-all duration-300",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        <Header setMobileOpen={setMobileOpen} />
        
        <main className="flex-1 p-4 md:p-6 overflow-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold">Mes Tâches</h1>
            <p className="text-muted-foreground">Gérez et suivez vos tâches assignées</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <StatCard title="Total" value={stats.total} icon={Target} color="bg-gray-100 text-gray-600" />
            <StatCard title="À faire" value={stats.pending} icon={Clock} color="bg-gray-100 text-gray-600" />
            <StatCard title="En cours" value={stats.in_progress} icon={Timer} color="bg-blue-100 text-blue-600" />
            <StatCard title="Terminées" value={stats.completed} icon={CheckCircle2} color="bg-green-100 text-green-600" />
            <StatCard title="En retard" value={stats.overdue} icon={AlertCircle} color="bg-red-100 text-red-600" />
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher une tâche..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="flex gap-2">
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-3 py-2 border rounded-lg text-sm"
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="pending">À faire</option>
                    <option value="in_progress">En cours</option>
                    <option value="review">En révision</option>
                    <option value="completed">Terminées</option>
                  </select>
                  <select
                    value={filterPriority}
                    onChange={(e) => setFilterPriority(e.target.value)}
                    className="px-3 py-2 border rounded-lg text-sm"
                  >
                    <option value="all">Toutes priorités</option>
                    <option value="urgent">Urgente</option>
                    <option value="high">Haute</option>
                    <option value="medium">Moyenne</option>
                    <option value="low">Basse</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tasks List */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredTasks.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Aucune tâche</h3>
                <p className="text-muted-foreground">Vous n'avez pas de tâches correspondant à ces critères.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredTasks.map((task) => {
                const status = STATUS_CONFIG[task.status] || STATUS_CONFIG.pending;
                const priority = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
                const dueDate = formatDate(task.due_date);
                const StatusIcon = status.icon;
                
                return (
                  <Card key={task.id} className="hover:shadow-md transition-shadow cursor-pointer">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className={cn("p-2 rounded-lg", status.color)}>
                          <StatusIcon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h3 className="font-semibold truncate">{task.title}</h3>
                              <p className="text-sm text-muted-foreground truncate">{task.description}</p>
                            </div>
                            <Badge className={priority.color}>{priority.label}</Badge>
                          </div>
                          <div className="flex items-center gap-4 mt-3 text-sm">
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <FolderKanban className="h-3.5 w-3.5" />
                              <span>{task.project_name}</span>
                            </div>
                            <div className={cn("flex items-center gap-1", dueDate.color)}>
                              <Calendar className="h-3.5 w-3.5" />
                              <span>{dueDate.text}</span>
                            </div>
                            {task.estimated_hours && (
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <Timer className="h-3.5 w-3.5" />
                                <span>{task.logged_hours || 0}h / {task.estimated_hours}h</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default TeamTasksPage;
